import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Package, Heart, User as UserIcon, Store } from "lucide-react";
import { shopApi, useShopAuth, errText } from "@/lib/shopAuth";
import { API } from "@/lib/api";
import { zl } from "@/lib/shop";
import ShopLayout from "./ShopLayout";
import ShopAuth from "./ShopAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ShopAccount() {
  const { customer, ready, logout, setCustomer } = useShopAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [favs, setFavs] = useState([]);
  const [stores, setStores] = useState([]);
  const [sh, setSh] = useState({});
  const [inv, setInv] = useState({});
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!customer) return;
    setName(customer.name || ""); setSh(customer.shipping || {}); setInv(customer.invoice || {});
    shopApi.get("/shop/auth/orders").then((r) => setOrders(r.data || [])).catch(() => {});
    shopApi.get("/shop/auth/favorites").then((r) => setFavs(r.data || [])).catch(() => {});
    shopApi.get("/b2b/my/stores").then((r) => setStores(r.data || [])).catch(() => setStores([]));
  }, [customer]);

  if (ready && !customer) return <ShopAuth />;
  if (!customer) return <ShopLayout><div className="py-20 text-center text-neutral-500">Ładowanie…</div></ShopLayout>;

  const saveProfile = async () => {
    setBusy(true);
    try { const { data } = await shopApi.put("/shop/auth/profile", { name, shipping: sh, invoice: inv }); setCustomer(data); toast.success("Zapisano dane"); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const si = "mt-1 bg-white/5 border-white/10 text-neutral-100";
  const S = (k, v) => setSh((p) => ({ ...p, [k]: v }));
  const I = (k, v) => setInv((p) => ({ ...p, [k]: v }));

  return (
    <ShopLayout>
      <div className="mx-auto max-w-4xl px-4 py-10" data-testid="shop-account">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            {customer.picture ? <img src={customer.picture} alt="" className="h-11 w-11 rounded-full" /> : <div className="grid h-11 w-11 place-items-center rounded-full bg-amber-400 text-[#0D1117]"><UserIcon className="h-5 w-5" /></div>}
            <div><p className="font-bold">{customer.name || "Moje konto"}</p><p className="text-xs text-neutral-400">{customer.email}</p></div>
          </div>
          <Button data-testid="shop-logout" onClick={() => { logout(); navigate("/sklep"); }} variant="outline" className="gap-2 border-white/20 text-neutral-200"><LogOut className="h-4 w-4" />Wyloguj</Button>
        </div>

        {stores.length > 0 && (
          <section className="mb-10" data-testid="account-my-stores">
            <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-amber-400 mb-3"><Store className="h-4 w-4" />Mój sklep</h2>
            <div className="grid gap-2 sm:grid-cols-2">
              {stores.map((s) => (
                <button key={s.id} data-testid={`account-store-${s.id}`} onClick={() => navigate("/sklep/moj-sklep")}
                  className="flex items-center justify-between rounded-lg border border-red-600/40 bg-red-600/10 p-4 text-left hover:bg-red-600/20 transition-colors">
                  <span className="font-bold uppercase tracking-wide">{s.name}</span>
                  <Store className="h-5 w-5 text-red-500" />
                </button>
              ))}
            </div>
          </section>
        )}

        <section className="mb-10">
          <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-amber-400 mb-3"><Package className="h-4 w-4" />Moje zamówienia</h2>
          {orders.length === 0 ? <p className="text-sm text-neutral-500">Brak zamówień.</p> : (
            <div className="space-y-2">
              {orders.map((o) => (
                <div key={o.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 p-3 text-sm" data-testid={`my-order-${o.id}`}>
                  <div><p className="font-semibold">{o.number}</p><p className="text-xs text-neutral-400">{new Date(o.created_at).toLocaleDateString("pl-PL")} · {o.status}</p></div>
                  <span className="font-bold">{zl(o.total_grosz)}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        {favs.length > 0 && (
          <section className="mb-10">
            <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-amber-400 mb-3"><Heart className="h-4 w-4" />Ulubione</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {favs.map((p) => (
                <button key={p.id} onClick={() => navigate(`/sklep/produkt/${p.id}`)} className="rounded-lg border border-white/10 overflow-hidden text-left">
                  <div className="aspect-square bg-white">{p.photos?.[0] && <img src={`${API}/files/${p.photos[0]}`} alt="" className="h-full w-full object-contain p-2" />}</div>
                  <div className="p-2"><p className="text-xs truncate">{p.name}</p><p className="text-sm font-bold">{zl(p.price_grosz)}</p></div>
                </button>
              ))}
            </div>
          </section>
        )}

        <section>
          <h2 className="text-sm font-bold uppercase tracking-wider text-amber-400 mb-3">Zapisane dane</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label className="text-xs">Imię i nazwisko</Label><Input value={name} onChange={(e) => setName(e.target.value)} className={si} /></div>
            <div><Label className="text-xs">Telefon</Label><Input value={sh.phone || ""} onChange={(e) => S("phone", e.target.value)} className={si} /></div>
            <div><Label className="text-xs">Ulica i numer</Label><Input value={sh.street || ""} onChange={(e) => S("street", e.target.value)} className={si} /></div>
            <div><Label className="text-xs">Kod pocztowy</Label><Input value={sh.postal || ""} onChange={(e) => S("postal", e.target.value)} className={si} /></div>
            <div><Label className="text-xs">Miejscowość</Label><Input value={sh.city || ""} onChange={(e) => S("city", e.target.value)} className={si} /></div>
            <div><Label className="text-xs">Firma (faktura)</Label><Input value={inv.company || ""} onChange={(e) => I("company", e.target.value)} className={si} /></div>
            <div><Label className="text-xs">NIP (faktura)</Label><Input value={inv.nip || ""} onChange={(e) => I("nip", e.target.value)} className={si} /></div>
          </div>
          <Button data-testid="save-profile" onClick={saveProfile} disabled={busy} className="mt-4 bg-amber-400 text-[#0D1117] font-bold uppercase">{busy ? "…" : "Zapisz dane"}</Button>
        </section>
      </div>
    </ShopLayout>
  );
}
